package app.footyguide.web.page

import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.css.*
import app.footyguide.web.components.*
import app.footyguide.web.data.StandingRow
import app.footyguide.web.data.StandingZone
import app.footyguide.web.route.WebRoutes
import kotlin.math.round

fun calculateWinPercentage(points: Int, played: Int): String {
    if (played <= 0) return "0%"
    val pct = round((points.toDouble() / (played * 3.0)) * 100).toInt()
    return "$pct%"
}

/**
 * Stitch Design System (Tabelas - Classificação Editorial).
 * Paper & Ink broad-sheet: #FAFAF7 substrate, #FFFFFF cards, #E7E7E1 hairline borders,
 * clear competition zones and responsive tabular layout.
 */
@Composable
fun StandingsPage(
    gameRepository: app.footyguide.domain.model.GameRepository = app.footyguide.web.data.WebGameRepository(),
    onNavigate: (String) -> Unit = {}
) {
    val strings = app.footyguide.core.locale.LocalStrings.current
    var standings by remember { mutableStateOf<List<StandingRow>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var selectedComp by remember { mutableStateOf("brasileirao") }

    LaunchedEffect(selectedComp) {
        isLoading = true
        standings = try {
            app.footyguide.web.data.WebStandingsRepository.getStandings(selectedComp, gameRepository)
        } catch (_: Exception) {
            emptyList()
        }
        isLoading = false
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            minHeight(100.vh)
            backgroundColor(Color("#FAFAF7"))
            paddingBottom(80.px)
        }
    }) {
        AppToolbar(
            icon = { LucideIcon(Icons.TROPHY, size = 20, color = "var(--accent-fill, #0B6E4F)") },
            title = strings.standings,
            actions = {
                val maxPlayed = standings.maxOfOrNull { it.played } ?: 0
                if (maxPlayed > 0) {
                    Span({
                        style {
                            fontSize(11.px)
                            fontFamily("Inter, sans-serif")
                            fontWeight(700)
                            color(Color("var(--accent-fill, #0B6E4F)"))
                            backgroundColor(Color("rgba(var(--accent-fill-rgb, 11, 110, 79), 0.12)"))
                            border(1.px, LineStyle.Solid, Color("rgba(var(--accent-fill-rgb, 11, 110, 79), 0.25)"))
                            padding(4.px, 8.px)
                            borderRadius(999.px)
                            property("text-transform", "uppercase")
                        }
                    }) {
                        Text(strings.formatRoundNumber(maxPlayed))
                    }
                }
            }
        )

        Div({
            style {
                width(100.percent)
                maxWidth(600.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                backgroundColor(Color("#FAFAF7"))
            }
        }) {
            // COMPETITION SELECTOR CHIPS
            Div({
                style {
                    margin(14.px, 16.px, 8.px)
                    display(DisplayStyle.Flex)
                    gap(8.px)
                    overflowX("auto")
                    property("scrollbar-width", "none")
                }
            }) {
                listOf(
                    Pair("brasileirao", "Brasileirão Série A"),
                    Pair("libertadores", "Libertadores"),
                    Pair("copadobrasil", "Copa do Brasil")
                ).forEach { (id, label) ->
                    val isSelected = selectedComp == id
                    Button({
                        onClick { selectedComp = id }
                        style {
                            minHeight(44.px)
                            padding(10.px, 16.px)
                            property("display", "inline-flex")
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                            backgroundColor(Color(if (isSelected) "#16181D" else "#FFFFFF"))
                            color(Color(if (isSelected) "#FFFFFF" else "#71717A"))
                            border(1.px, LineStyle.Solid, Color(if (isSelected) "#16181D" else "#E7E7E1"))
                            borderRadius(999.px)
                            fontFamily("Inter, sans-serif")
                            fontSize(12.px)
                            fontWeight(if (isSelected) 700 else 600)
                            cursor("pointer")
                            whiteSpace("nowrap")
                        }
                    }) {
                        Text(label)
                    }
                }
            }

            // STANDINGS TABLE
            Div({ style { padding(8.px, 16.px, 16.px) } }) {
                if (isLoading) {
                    StandingsTableSkeleton()
                } else if (standings.isEmpty()) {
                    Div({
                        style {
                            padding(40.px, 24.px)
                            backgroundColor(Color("#FFFFFF"))
                            border(1.px, LineStyle.Solid, Color("#E7E7E1"))
                            borderRadius(16.px)
                            display(DisplayStyle.Flex)
                            flexDirection(FlexDirection.Column)
                            alignItems(AlignItems.Center)
                            gap(10.px)
                            property("text-align", "center")
                        }
                    }) {
                        LucideIcon(Icons.TROPHY, size = 28, color = "#71717A")
                        Span({
                            style {
                                fontFamily("Sora, sans-serif")
                                fontSize(15.px)
                                fontWeight(800)
                                color(Color("#16181D"))
                            }
                        }) {
                            Text(strings.standingsUnavailableTitle)
                        }
                        Span({
                            style {
                                fontFamily("Inter, sans-serif")
                                fontSize(13.px)
                                color(Color("#71717A"))
                            }
                        }) {
                            Text(strings.standingsUnavailableSubtitle)
                        }
                    }
                } else {
                Div({
                    style {
                        backgroundColor(Color("#FFFFFF"))
                        border(1.px, LineStyle.Solid, Color("#E7E7E1"))
                        borderRadius(16.px)
                        overflow("hidden")
                        property("box-shadow", "0 2px 8px rgba(22, 24, 29, 0.04)")
                    }
                }) {
                    // TABLE HEADER
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            padding(10.px, 12.px)
                            backgroundColor(Color("#FAFAF7"))
                            property("border-bottom", "1px solid #E7E7E1")
                            fontSize(11.px)
                            fontWeight(800)
                            fontFamily("Inter, sans-serif")
                            color(Color("#71717A"))
                            property("text-transform", "uppercase")
                        }
                    }) {
                        Span({ style { width(32.px) } }) { Text("#") }
                        Span({ style { flex(1) } }) { Text(strings.tableClub) }
                        Span({ style { width(36.px); property("text-align", "center") } }) { Text(strings.tablePointsShort) }
                        Span({ style { width(28.px); property("text-align", "center") } }) { Text(strings.tablePlayedShort) }
                        Span({ style { width(28.px); property("text-align", "center") } }) { Text(strings.tableWonShort) }
                        Span({ style { width(32.px); property("text-align", "center") } }) { Text(strings.tableGoalDiffShort) }
                    }

                    // TABLE ROWS
                    standings.forEach { row ->
                        val zoneBarColor = when (row.zone) {
                            StandingZone.LIBERTADORES -> "#059669"
                            StandingZone.PRE_LIBERTADORES -> "#10B981"
                            StandingZone.SUL_AMERICANA -> "#0284C7"
                            StandingZone.RELEGATION -> "#DC2626"
                            StandingZone.NEUTRAL -> "transparent"
                        }

                        Div({
                            onClick { onNavigate(WebRoutes.club(row.teamId)) }
                            style {
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                padding(9.px, 12.px)
                                property("border-bottom", "1px solid #F4F4F5")
                                backgroundColor(Color("#FFFFFF"))
                                property("border-left", "3px solid $zoneBarColor")
                                fontSize(13.px)
                                fontFamily("Inter, sans-serif")
                                cursor("pointer")
                                property("transition", "background-color 0.15s ease")
                            }
                        }) {
                            Span({
                                style {
                                    width(32.px)
                                    fontWeight(700)
                                    fontFamily("Sora, sans-serif")
                                    fontSize(12.px)
                                    color(Color("#71717A"))
                                }
                            }) {
                                Text("${row.position}")
                            }

                            Div({
                                style {
                                    flex(1)
                                    display(DisplayStyle.Flex)
                                    alignItems(AlignItems.Center)
                                    gap(8.px)
                                }
                            }) {
                                SafeImg(
                                    src = row.logoUrl,
                                    fallbackIcon = Icons.SHIELD,
                                    fallbackSize = CrestSizes.STANDINGS_ROW_FALLBACK,
                                    loading = "eager",
                                    modifier = {
                                        width(CrestSizes.STANDINGS_ROW_PX.px)
                                        height(CrestSizes.STANDINGS_ROW_PX.px)
                                        property("width", CrestSizes.STANDINGS_ROW_CSS)
                                        property("height", CrestSizes.STANDINGS_ROW_CSS)
                                        property("object-fit", "contain")
                                    }
                                )
                                Span({
                                    style {
                                        fontWeight(600)
                                        color(Color("#27272A"))
                                    }
                                }) {
                                    Text(row.name)
                                }
                            }

                            Span({
                                style {
                                    width(36.px)
                                    fontWeight(800)
                                    fontFamily("Sora, sans-serif")
                                    fontSize(13.px)
                                    color(Color("#16181D"))
                                    property("text-align", "center")
                                }
                            }) {
                                Text("${row.points}")
                            }

                            Span({
                                style { width(28.px); color(Color("#71717A")); fontSize(12.px); property("text-align", "center") }
                            }) {
                                Text("${row.played}")
                            }

                            Span({
                                style { width(28.px); color(Color("#71717A")); fontSize(12.px); property("text-align", "center") }
                            }) {
                                Text("${row.wins}")
                            }

                            Span({
                                style { width(32.px); color(Color("#71717A")); fontSize(12.px); property("text-align", "center") }
                            }) {
                                Text("${row.goalDifference}")
                            }
                        }
                    }
                }

                // LEGEND
                Div({
                    style {
                        marginTop(12.px)
                        padding(12.px, 16.px)
                        backgroundColor(Color("#FFFFFF"))
                        border(1.px, LineStyle.Solid, Color("#E7E7E1"))
                        borderRadius(12.px)
                        display(DisplayStyle.Flex)
                        flexWrap(FlexWrap.Wrap)
                        gap(12.px)
                        fontSize(11.px)
                        fontFamily("Inter, sans-serif")
                    }
                }) {
                    Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(6.px) } }) {
                        Span({ style { width(8.px); height(8.px); borderRadius(4.px); backgroundColor(Color("#059669")) } })
                        Span({ style { color(Color("#71717A")) } }) { Text(strings.zoneLibertadores) }
                    }
                    Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(6.px) } }) {
                        Span({ style { width(8.px); height(8.px); borderRadius(4.px); backgroundColor(Color("#10B981")) } })
                        Span({ style { color(Color("#71717A")) } }) { Text(strings.zonePreLibertadores) }
                    }
                    Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(6.px) } }) {
                        Span({ style { width(8.px); height(8.px); borderRadius(4.px); backgroundColor(Color("#0284C7")) } })
                        Span({ style { color(Color("#71717A")) } }) { Text(strings.zoneSulAmericana) }
                    }
                    Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(6.px) } }) {
                        Span({ style { width(8.px); height(8.px); borderRadius(4.px); backgroundColor(Color("#DC2626")) } })
                        Span({ style { color(Color("#71717A")) } }) { Text(strings.zoneRelegation) }
                    }
                }
                }
            }
        }

        // BOTTOM NAV
        BottomNavBar(
            currentRoute = WebRoutes.STANDINGS,
            onNavigate = onNavigate
        )
    }
}

@Composable
private fun StandingsTableSkeleton() {
    Div({
        style {
            backgroundColor(Color("#FFFFFF"))
            border(1.px, LineStyle.Solid, Color("#E7E7E1"))
            borderRadius(16.px)
            overflow("hidden")
        }
    }) {
        repeat(10) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    padding(12.px)
                    gap(10.px)
                    property("border-bottom", "1px solid #F4F4F5")
                }
            }) {
                Div({
                    classes("fg-skeleton-shimmer")
                    style { width(20.px); height(14.px); borderRadius(4.px) }
                })
                Div({
                    classes("fg-skeleton-shimmer")
                    style { width(20.px); height(20.px); borderRadius(10.px) }
                })
                Div({
                    classes("fg-skeleton-shimmer")
                    style { flex(1); height(14.px); borderRadius(4.px) }
                })
                Div({
                    classes("fg-skeleton-shimmer")
                    style { width(110.px); height(14.px); borderRadius(4.px) }
                })
            }
        }
    }
}
